<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  AdminOrganizationViewCreateMutation,
  AdminOrganizationViewCreateMutationVariables,
  AdminOrganizationViewDataQuery,
  AdminOrganizationViewDataQueryVariables,
  AdminOrganizationViewUpdateMutation,
  AdminOrganizationViewUpdateMutationVariables,
  CreateOrganization,
  Mutation,
  UpdateOrganization,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import ItemEditIcon from '@/components/button/ButtonEdit.vue'
import ItemDeleteIcon from '@/components/button/ButtonDelete.vue'
import TextField from '@/components/input/TextField.vue'
import OrganizationSlug from '@/components/input/OrganizationSlug.vue'
import { normalName, required } from '@/validation'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import { useRouter } from 'vue-router'
import { mockCreateOrganization, mockUpdateOrganization } from '@/generated/graphql-mocks'
import slugify from 'slugify'

type LocalOrg = AdminOrganizationViewDataQuery['account']['adminOrganizations'][0]
const { t } = useI18n()

const fetchQuery = useQuery<
  AdminOrganizationViewDataQuery,
  AdminOrganizationViewDataQueryVariables
>(gql`
  query AdminOrganizationViewData {
    account {
      adminOrganizations {
        id
        name
        slug
      }
    }
  }
`)
const items = computed(() => fetchQuery.result.value?.account.adminOrganizations || [])

type CreateModel = Partial<CreateOrganization>
const createModelKeys = Object.keys(mockCreateOrganization())
const createModel = reactive<CreateModel>({})
const createValidation = useVuelidate<CreateModel>({ name: { required, normalName } }, createModel)

const syncSlug = () => {
  createModel.slug = slugify(createModel.name || '', { lower: true })
}
const isSlugInSync = ref(true)
watch(
  () => [createModel.name, isSlugInSync.value],
  () => {
    if (isSlugInSync.value) {
      syncSlug()
    }
  },
)

const createMutation = useMutation<
  AdminOrganizationViewCreateMutation,
  AdminOrganizationViewCreateMutationVariables
>(gql`
  mutation AdminOrganizationViewCreate($command: CreateOrganization!) {
    account {
      adminCreateOrganization(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  createModelKeys.forEach((k) => (createModel[k as keyof CreateModel] = undefined))
  isSlugInSync.value = true
  await createDialog.value?.open(() =>
    createMutation
      .mutate({ command: createModel as CreateOrganization })
      .then(() => fetchQuery.refetch()),
  )
}

type UpdateModel = Partial<UpdateOrganization>
const updateModelKeys = Object.keys(mockUpdateOrganization())
const updateModel = reactive<UpdateModel>({})
const updateValidation = useVuelidate<UpdateModel>({ name: { required, normalName } }, updateModel)
const updateMutation = useMutation<
  AdminOrganizationViewUpdateMutation,
  AdminOrganizationViewUpdateMutationVariables
>(gql`
  mutation AdminOrganizationViewUpdate($command: UpdateOrganization!) {
    account {
      adminUpdateOrganization(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalOrg) => {
  updateModelKeys.forEach((k) => {
    ;(updateModel as Record<string, unknown>)[k] = item[k as keyof LocalOrg]
  })
  await updateDialog.value?.open(() =>
    updateMutation
      .mutate({ command: updateModel as UpdateOrganization })
      .then(() => fetchQuery.refetch()),
  )
}

const deleteMutation = useMutation<Mutation>(gql`
  mutation AdminOrganizationViewDelete($id: ID!) {
    account {
      adminDeleteOrganization(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const doDelete = async (item: LocalOrg) => {
  await deleteDialog.value?.open(item.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

const router = useRouter()
const onSelectOrganization = (_: unknown, row: { item: LocalOrg }) => {
  router.push({ name: 'orgHome', params: { organizationSlug: row.item.slug } })
}

const search = ref('')
const headers = [
  {
    key: 'name',
    title: t('entity.organization.field.name'),
  },
  {
    key: 'slug',
    title: t('entity.organization.field.slug'),
  },
  {
    key: 'actions',
    width: '100',
    sortable: false,
    align: 'end',
  },
]
const sort = [{ key: 'name', order: 'asc' }]
</script>

<template>
  <h1>{{ t('entity.organization.plural') }}</h1>

  <data-table
    :items="items"
    :headers="headers"
    :loading="fetchQuery.loading"
    :sort-by="sort"
    :search="search"
    density="comfortable"
    @click:row="onSelectOrganization"
  >
    <template #header.actions>
      <data-table-search v-model="search" />
      <data-table-create-button @click="doCreate" />
    </template>
    <template #item.actions="{ item }: { item: LocalOrg }">
      <item-edit-icon @click="doUpdate(item)" />
      <item-delete-icon @click="doDelete(item)" />
    </template>
  </data-table>

  <async-delete-dialog
    ref="deleteDialog"
    :warning="t('entity.organization.deleteWarning')"
    hindrance
  />

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.organization.singular')"
    :validation="createValidation"
  >
    <text-field
      v-model="createModel.name"
      :label="t('entity.organization.field.name')"
      required
      :validation="createValidation.name"
    />
    <organization-slug
      v-model="createModel.slug"
      :label="t('entity.organization.field.slug')"
      required
      :readonly="isSlugInSync"
    >
      <template #append>
        <v-btn
          :icon="isSlugInSync ? 'link' : 'link_off'"
          density="compact"
          variant="flat"
          @click="isSlugInSync = !isSlugInSync"
        />
      </template>
    </organization-slug>
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.organization.singular')"
    :validation="updateValidation"
  >
    <text-field
      v-model="updateModel.name"
      :label="t('entity.organization.field.name')"
      required
      :validation="updateValidation.name"
    />
    <organization-slug
      v-model="updateModel.slug"
      :label="t('entity.organization.field.slug')"
      required
      :current-org-id="updateModel.id!"
    />
  </upsert-dialog>
</template>

<style scoped lang="scss"></style>
